
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import ActiveUserMixin from '~/mixins/activeUserMixin'
import RepoDetailMixin from '~/mixins/repoDetailMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'
import RouteParamsMixin from '~/mixins/routeParamsMixin'

import { OwnerDetailActions, OwnerDetailGetters } from '~/store/owner/detail'

import { AppFeatures, RepoPerms, TeamPerms } from '~/types/permTypes'
import {
  CodeAnalysisStatus,
  PrStateChoices,
  Repository,
  RepositoryKindChoices,
  ScaStatus
} from '~/types/types'

interface TabLink {
  icon: string
  label: string
  link?: string
  loginRequired?: boolean
  matchName?: string[]
  perms?: RepoPerms[]
  pattern?: RegExp
  gateFeature?: AppFeatures[]
  forBetaUser?: boolean
  forBetaOwner?: boolean
  teamOnly?: boolean
  hideOnPrem?: boolean
  repoKindChoices: RepositoryKindChoices[]
}

export default defineComponent({
  name: 'RepoHeader',
  mixins: [RouteParamsMixin, RepoDetailMixin, RoleAccessMixin, ActiveUserMixin],
  async fetch(): Promise<void> {
    await this.fetchRepoPerms(this.baseRouteParams)

    if (this.isMonorepo) {
      return
    }

    await Promise.all([
      this.fetchMetrics({ ...this.baseRouteParams, fetchPerms: true }),
      this.fetchOwnerDetails({
        login: this.owner,
        provider: this.provider
      })
    ])

    //? Pre-load first link of metric
    const firstMetric = this.repository.metricsCaptured?.[0]
    if (firstMetric) {
      const metricRecord = this.navItems.findIndex((navItem) => navItem.label === 'Metrics')
      this.navItems[metricRecord].link = `metrics/${firstMetric.shortcode}`
    }
  },
  methods: {
    ...mapActions('owner/detail', {
      fetchOwnerDetails: OwnerDetailActions.FETCH_OWNER_DETAILS
    }),

    activeLink(item: TabLink): boolean {
      if (item.pattern && this.$route.name) {
        return item.pattern.test(this.$route.name)
      }
      return false
    },

    isNavLinkVisible(item: TabLink): boolean {
      const isItemValidForRepoKind = item.repoKindChoices.includes(this.repoKind)

      if (!isItemValidForRepoKind) {
        return false
      }

      if (item.loginRequired && !this.loggedIn) {
        return false
      }

      if (item.hideOnPrem && this.$config.onPrem) {
        return false
      }

      const allowedForProvider = item.gateFeature
        ? this.$gateKeeper.provider(item.gateFeature, this.provider)
        : true

      const allowedForRepo = item.perms
        ? this.$gateKeeper.repo(item.perms, this.repoPerms.permission)
        : true

      const allowForBetaOwner = item.forBetaOwner ? Boolean(this.isOwnerBetaTester) : true

      const allowForBetaUser =
        this.$config.onPrem || (item.forBetaUser ? Boolean(this.viewer.isBetaTester) : true)

      const teamOnly = item.teamOnly ? Boolean(this.isOwnerATeam) : true

      return (
        allowedForRepo &&
        allowedForProvider &&
        teamOnly &&
        (item.forBetaOwner ? allowForBetaOwner : allowForBetaUser)
      )
    },
    triggerExpand() {
      this.$root.$emit('ui:show-sidebar-menu')
    },
    async toggleStar(isStarred: boolean) {
      const { id, displayName, isPrivate, kind, name, vcsProvider }: Repository = this.repository

      const response = await this.updateStarredRepo({
        action: isStarred ? 'ADD' : 'REMOVE',
        repo: { id, displayName, isPrivate, kind, name, vcsProvider, ownerLogin: this.owner }
      })

      if (!response?.ok) {
        this.updateRepositoryInStore({ ...this.repository, isStarred: !isStarred })
        this.$toast.danger(
          "Couldn't star this repository, if the issue persists please contact support"
        )
      }

      this.fetchBasicRepoDetails({
        ...this.baseRouteParams,
        refetch: true
      })
    }
  },
  computed: {
    ...mapGetters('owner/detail', {
      isOwnerBetaTester: OwnerDetailGetters.IS_BETA_TESTER,
      isOwnerATeam: OwnerDetailGetters.IS_TEAM
    }),
    canReadTeamPage(): boolean {
      if (this.teamPerms.permission && this.activeOwner === this.owner) {
        return this.$gateKeeper.team(TeamPerms.VIEW_TEAM_HOME, this.teamPerms.permission)
      }
      return false
    },
    icon() {
      const { isPrivate } = this.repository

      if (this.isSubRepository) {
        return isPrivate ? 'folder-locked' : 'folder-globe'
      }

      if (this.repoKind === RepositoryKindChoices.Repo) {
        return isPrivate ? 'z-lock' : 'globe'
      }

      return 'monorepo'
    },
    isMonorepo(): boolean {
      return this.repoKind === RepositoryKindChoices.Monorepo
    },
    isSubRepository(): boolean {
      return this.repoKind === RepositoryKindChoices.Subrepo
    },
    monorepoName(): string[] {
      return [this.repository.parentMonorepo?.name ?? '']
    },
    monorepoDisplayName(): string {
      return this.repository.parentMonorepo?.displayName ?? ''
    },

    navItems(): TabLink[] {
      return [
        {
          icon: this.isMonorepo ? 'folder-tree' : 'gauge',
          label: this.isMonorepo ? 'Sub-repositories' : 'Overview',
          pattern: new RegExp(/^provider-owner-repo$/),
          repoKindChoices: [
            RepositoryKindChoices.Repo,
            RepositoryKindChoices.Monorepo,
            RepositoryKindChoices.Subrepo
          ]
        },
        {
          icon: 'flag',
          label: 'Issues',
          link: 'issues',
          pattern: new RegExp(/^provider-owner-repo-issue/),
          repoKindChoices: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo]
        },
        {
          icon: 'blocks-1',
          label: 'Dependencies',
          link: 'dependencies',
          loginRequired: true,
          teamOnly: true,
          pattern: new RegExp(/^provider-owner-repo-dependencies/),
          repoKindChoices: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo]
        },
        {
          icon: 'bar-chart',
          label: 'Metrics',
          link: 'metrics',
          pattern: new RegExp(/^provider-owner-repo-metrics-/),
          repoKindChoices: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo]
        },
        {
          icon: 'pie-chart',
          label: 'Reports',
          link: 'reports/owasp-top-10',
          loginRequired: true,
          perms: [RepoPerms.VIEW_REPORTS],
          pattern: new RegExp(/^provider-owner-repo-reports-/),
          repoKindChoices: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo]
        },
        {
          icon: 'history',
          label: 'History',
          link: `history/pull-requests?state=${PrStateChoices.Open}`,
          pattern: new RegExp(/^provider-owner-repo-(history|run)/),
          repoKindChoices: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo]
        },
        {
          icon: 'memory',
          label: 'Knowledge',
          link: 'knowledge',
          loginRequired: true,
          // TODO(ENG-4617): revisit once individual (uin) accounts are supported — pending product.
          teamOnly: true,
          hideOnPrem: true,
          perms: [RepoPerms.VIEW_KNOWLEDGE],
          pattern: new RegExp(/^provider-owner-repo-knowledge/),
          repoKindChoices: [RepositoryKindChoices.Repo, RepositoryKindChoices.Subrepo]
        },
        {
          icon: 'settings',
          label: 'Settings',
          link: 'settings/general',
          loginRequired: true,
          perms: [
            RepoPerms.GENERATE_SSH_KEY_PAIR,
            RepoPerms.CHANGE_DEFAULT_ANALYSIS_BRANCH,
            RepoPerms.CHANGE_ISSUE_TYPES_TO_REPORT,
            RepoPerms.CHANGE_ISSUES_TO_TYPE_TO_BLOCK_PRS_ON,
            RepoPerms.DEACTIVATE_ANALYSIS_ON_REPOSITORY,
            RepoPerms.ADD_REMOVE_MEMBERS,
            RepoPerms.UPDATE_ROLE_OF_EXISTING_MEMBERS,
            RepoPerms.VIEW_AUDIT_LOG
          ],
          pattern: new RegExp(/^provider-owner-repo-settings-/),
          repoKindChoices: [
            RepositoryKindChoices.Repo,
            RepositoryKindChoices.Monorepo,
            RepositoryKindChoices.Subrepo
          ]
        }
      ]
    },
    repoKind(): RepositoryKindChoices {
      return this.repository.kind
    },
    repoVCSIcon(): string {
      const provider = this.repository.vcsProvider
      return this.$providerMetaMap[provider].icon ?? ''
    },
    showMetadataViewToggleButton(): boolean {
      return this.repository.isActivated && this.$route.name === 'provider-owner-repo'
    },
    subRepositoryPath(): string {
      // Check if monorepoDisplayName is defined and not empty.
      if (this.monorepoDisplayName && this.repository?.displayName) {
        const parts = this.repository.displayName.split(this.monorepoDisplayName)
        return parts.length > 1 ? parts[1] : ''
      }
      return ''
    },
    teamPageUrl(): string {
      const { provider, owner } = this.$route.params
      return ['', provider, owner].join('/')
    },
    allowStar(): boolean {
      if (this.loggedIn) {
        return this.$gateKeeper.repo(RepoPerms.ALLOW_STAR, this.repoPerms.permission)
      }
      return false
    },
    codeAnalysisTagState(): string {
      const { codeAnalysisStatus } = this.repository
      switch (codeAnalysisStatus) {
        case CodeAnalysisStatus.Active:
          return 'juniper'
        case CodeAnalysisStatus.Inactive:
          return 'vanilla-dark'
        case CodeAnalysisStatus.Error:
          return 'cherry'
        default:
          return 'vanilla-dark'
      }
    },
    codeAnalysisTagText() {
      const { codeAnalysisStatus } = this.repository
      switch (codeAnalysisStatus) {
        case CodeAnalysisStatus.Active:
          return 'CODE ANALYSIS ACTIVE'
        case CodeAnalysisStatus.Inactive:
          return 'CODE ANALYSIS INACTIVE'
        case CodeAnalysisStatus.Error:
          return 'CODE ANALYSIS ERROR'
        default:
          return 'CODE ANALYSIS INACTIVE'
      }
    },
    scaTagState(): string {
      const { scaStatus } = this.repository
      switch (scaStatus) {
        case ScaStatus.Active:
          return 'robin'
        case ScaStatus.Inactive:
          return 'vanilla-dark'
        default:
          return 'vanilla-dark'
      }
    },
    scaTagText() {
      const { scaStatus } = this.repository
      switch (scaStatus) {
        case ScaStatus.Active:
          return 'SCA ACTIVE'
        case ScaStatus.Inactive:
          return 'SCA INACTIVE'
        default:
          return 'SCA INACTIVE'
      }
    }
  }
})
